import type { FocusEvent, KeyboardEvent, MouseEvent } from 'react'
import { Link } from 'react-router'

import { Icon } from '@/components/icons/Icon'
import type { Suggestion } from '@/types/api'

import { suggestionHref, suggestionRows } from './suggestions'
import s from './SuggestList.module.css'

const HINT: Record<Suggestion['kind'], string> = {
  query: '',
  category: 'Category',
  shop: 'Shop',
  collection: 'Collection',
}

/**
 * Suggestions under the search box (D-91): product names fill the box, categories, shops and
 * collections open their page. Real links and buttons: the arrow keys move the focus between
 * them, Escape goes back to the field (`onEscape`).
 */
export function SuggestList({
  items,
  onPick,
  onEscape,
  onBlur,
}: {
  items: Suggestion[]
  onPick: (item: Suggestion) => void
  onEscape: () => void
  /** The focus left a row (to close the list when it left the search box too). */
  onBlur: (e: FocusEvent<HTMLElement>) => void
}) {
  // Keep the field focused while tapping a row (else its blur closes the list first).
  const keepFocus = (e: MouseEvent) => e.preventDefault()
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    const rows = suggestionRows()
    const i = rows.indexOf(e.currentTarget)
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      const next = i + (e.key === 'ArrowDown' ? 1 : -1)
      if (next < 0) onEscape()
      else rows[Math.min(next, rows.length - 1)]?.focus()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onEscape()
    }
  }
  return (
    <ul id="search-suggest" aria-label="Suggestions" className={s.list}>
      {items.map((item, i) => {
        const href = suggestionHref(item)
        const body = (
          <>
            <span className={s.icon} aria-hidden="true">
              {item.kind === 'collection' && item.emoji ? (
                item.emoji
              ) : (
                <Icon
                  name={item.kind === 'shop' ? 'store' : item.kind === 'query' ? 'search' : 'tag'}
                  size={16}
                />
              )}
            </span>
            <span className={s.text}>
              {item.text}
              {item.kind === 'category' && item.text_ml && (
                <span className={s.ml} lang="ml">
                  {' '}
                  · {item.text_ml}
                </span>
              )}
            </span>
            {HINT[item.kind] && <span className={s.hint}>{HINT[item.kind]}</span>}
          </>
        )
        return (
          <li key={`${item.kind}-${item.text}-${i}`}>
            {href ? (
              <Link
                to={href}
                className={s.btn}
                data-suggestion=""
                onKeyDown={onKeyDown}
                onMouseDown={keepFocus}
                onBlur={onBlur}
                onClick={() => onPick(item)}
              >
                {body}
              </Link>
            ) : (
              <button
                type="button"
                className={s.btn}
                data-suggestion=""
                onKeyDown={onKeyDown}
                onMouseDown={keepFocus}
                onBlur={onBlur}
                onClick={() => onPick(item)}
              >
                {body}
              </button>
            )}
          </li>
        )
      })}
    </ul>
  )
}
